import { useEffect, useRef, useState } from 'react';

import getConfig from 'next/config';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Text, Icon, Flexbox, Button, FormatDate, InformationBanner } from '@morressier/ts';

import { FaEye as ViewsIcon, FaDownload as DownloadIcon } from 'react-icons/fa';
import Grid from 'styled-components-grid';
import useSWR from 'swr';

import { getOrgPage } from 'api/organizations';
import { fetchPosterStats } from 'api/poster';
import { BuyATicketButton } from 'components/BuyTicketModal/BuyATicketButton';
import { useEventAccess } from 'components/EventStandardContainer/hooks';
import MorressierMarkdown from 'components/MorressierMarkdown';
import SpinnerIcon from 'components/Spinner';
import { FeatureFlags } from 'config/featureFlags';
import { DPC_EVENT_ORIGIN } from 'constants/general';
import { CommentsContainer } from 'containers/SingleSession/CommentsContainer';
import { useAuthedUser } from 'hooks/useIsAuthedUser';
import { useLocation } from 'hooks/useLocation';
import { useSubmissionType } from 'hooks/useSubmissionType';
import {
  fetchDocument,
  fetchDocumentAuthorsByIDsV3,
  FetchDocumentPathResponse,
  FetchDocumentType,
  fetchDocumentV2,
  fetchPosterAuthors,
  getDownloadPosterUrl,
  getSignedDatasetUrl,
} from 'services/documents';
import { useAuthContext, AuthState } from 'store';
import { logEvent } from 'utils/eventTracking';
import { isObjectId } from 'utils/validations';

import { PageProps } from '../../pages/event/[eventId]/article/[article_id]';
import HTMLtext from '../HTMLtext/index';
import ArticleContents from './ArticleContents';
import ArticleSection from './ArticleSection';
import Authors from './Authors';
import DatasetsSection from './DatasetsSection';
import DOISection from './DOISection';
import LicenseSection from './LicenseSection';
import OrganizationLink from './OrganizationLink';
import PresentationSection from './PresentationSection';
import StatusChipArticle from './StatusChipArticle';
import * as Styles from './styles';
import TopSection from './TopSection';
import VideoSection from './VideoSection';

const { publicRuntimeConfig } = getConfig();

const FREE_CONTENT_FILTER = 'Free Content';

const ArticleStatistics = props => {
  const { stats } = props;
  const articleStatistics = [
    {
      titleId: 'views',
      icon: ViewsIcon,
      quantity: stats?.views || 0,
    },
    {
      titleId: 'downloads',
      icon: DownloadIcon,
      quantity: stats?.downloads || 0,
    },
    // {
    //   titleId: 'saves',
    //   icon: SavesIcon,
    //   quantity: stats?.bookmarks,
    // },
  ];
  return (
    <>
      {articleStatistics.map(statistic => (
        <Styles.ArticleStatistics key={statistic.titleId}>
          <Icon as={statistic.icon} />
          {`${statistic.quantity} ${statistic.titleId}`}
        </Styles.ArticleStatistics>
      ))}
    </>
  );
};

const PROCESSING_STATE_DONE = 2;
const useResolveArticlePath = (article_id: string, isEmbargoed: boolean) => {
  const isAuthed = useAuthedUser();
  const { repo_api } = publicRuntimeConfig;
  const url = isEmbargoed && !isAuthed ? null : `${repo_api}/documents/${article_id}`;

  const { data } = useSWR<FetchDocumentPathResponse>(url, { revalidateOnFocus: false });

  if (data) {
    const { organization_id = 'no_defined', event_id, document_id } = data;
    return { organization_id, event_id, document_id, article_id };
  }

  return null;
};

const useFetchDocument = (
  article_id: string,
  isEmbargoed: boolean,
  fallbackData: ResolvedPromise<ReturnType<typeof fetchDocument>> | null,
  authState?: AuthState,
) => {
  const articlePath = useResolveArticlePath(article_id, isEmbargoed);

  // Wait for authentication to initialize before requesting document data
  // Authenticated user might have access to full document if embargo in on
  const documentFetchParams =
    authState?.isInit && articlePath
      ? [articlePath.organization_id, articlePath.event_id, articlePath.document_id]
      : null;

  const { data } = useSWR(documentFetchParams, fetchDocumentV2, {
    fallbackData,
    revalidateOnFocus: false,
  });

  const { data: posterUrl } = useSWR<string>(data?.id ? [data?.id] : null, getDownloadPosterUrl, {
    revalidateOnFocus: false,
  });

  const [authors, setAuthors] = useState<
    ResolvedPromise<ReturnType<typeof fetchDocumentAuthorsByIDsV3>>
    // @ts-ignore This is actually of the type MorressierUser.
  >(() => (fallbackData?.authors?.length ? fallbackData?.authors : []));

  const [signedDatasetUrls, setSignedDatasetUrls] = useState<string[]>([]);

  const [loadingAuthors, setLoadingAuthors] = useState<boolean>(
    () => !fallbackData?.authors.length,
  );

  useEffect(() => {
    if (data?.additional_file_urls) {
      const getUrls = async () => {
        const signedURLs = await getSignedDatasetUrl(article_id);
        setSignedDatasetUrls(signedURLs);
      };
      getUrls();
    }
  }, [data?.additional_file_urls, article_id]);

  useEffect(() => {
    let timeout;
    if (authors.length) return;
    if (data?.authors.length && typeof data?.authors[0] === 'string') {
      const getAuthors = async () => {
        // resolve up to 5 article authors
        try {
          const authorsResult = await fetchPosterAuthors(article_id);
          setAuthors(authorsResult);
        } finally {
          setLoadingAuthors(false);
        }
      };
      getAuthors();
    } else {
      timeout = setTimeout(() => setLoadingAuthors(false), 5000); // remove loader text
    }
    // eslint-disable-next-line consistent-return
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [authors.length, data?.authors]);

  return {
    ...data,
    additional_file_urls: signedDatasetUrls,
    organization_id: articlePath?.organization_id,
    pdfUrl: data?.pdfUrl || posterUrl,
    authors,
    loadingAuthors,
  } as FetchDocumentType & {
    authors: MorressierUser[];
    loadingAuthors?: boolean;
    additional_filters?: { content_type: string[] };
  };
};

const Article: React.FC<PageProps['data']> = props => {
  const { event_id, article_id, event, videoSession } = props;
  const titleEl = useRef<HTMLDivElement>(null);
  const videoRef = useRef(null);
  const abstractRef = useRef(null);
  const posterRef = useRef(null);
  const datasetsRef = useRef(null);
  const submissionType = useSubmissionType(event_id);
  const { state: authState } = useAuthContext();
  const isAuthenticated = !!authState?.profile?.email;
  const origin = useLocation()?.origin;

  const timeRef = useRef(Date.now());
  const router = useRouter();

  const documentData = useFetchDocument(
    article_id,
    false, // always fetch data - WF-2010 - isEmbargoed,
    props.document,
    authState,
  );

  const authUser = authState?.profile;
  const userEvents = authState?.userEvents || [];
  const isPosterAuthor = (documentData?.authors as MorressierUser[]).some(
    a => a?._id === authUser?.id,
  );
  const isOrganizer = userEvents?.some(ue => ue.id === event_id && ue.roles.includes('organizer'));
  const [orgData, setOrgData] = useState<MorressierOrganization>();
  const [stats, setStats] = useState<MorressierPosterStats | null>(null);

  const { eventAccess, isLoadingAccess } = useEventAccess(props.event.id);

  // Always show article view for organizers
  const isEmbargoed = (props.event.embargoed || documentData?.embargoed) && !eventAccess?.hasAccess;
  const isTicketingProduct =
    FeatureFlags.Event_Ticketing.isEnabled() && props.ticketingProduct.ticketEnabled;
  const isFreeContent =
    documentData?.additional_filters?.content_type?.includes(FREE_CONTENT_FILTER);

  const pdfUrl = isEmbargoed ? '' : documentData?.pdfUrl;
  const isDPCEvent = documentData?.origin === DPC_EVENT_ORIGIN;
  const { authors, loadingAuthors } = documentData;
  const keywords = props.data.articleKeywords;
  const abstract = documentData?.paper_abstract_markdown || documentData?.paper_abstract;

  useEffect(() => {
    fetchPosterStats(event_id, article_id).then(response => {
      setStats(response);
    });
  }, []);

  useEffect(() => {
    logEvent('STANDALONE_VISITED_SUBMISSION_PAGE');
  }, []);

  useEffect(() => {
    const handleLogArticleView = () => {
      if (documentData?.processing_state === PROCESSING_STATE_DONE) {
        logEvent('VIEWED_ARTICLE_PAGE', {
          id: documentData?.id,
          viewedFor: (Date.now() - timeRef.current) / 1000,
          isAuthenticated,
        });
      }
    };
    const handleLogPrint = () => {
      if (documentData) {
        logEvent('ARTICLE_PRINT', { id: documentData?.id });
      }
    };
    const handleRouteChange = () => {
      handleLogArticleView();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    window.addEventListener('beforeunload', handleLogArticleView);
    window.addEventListener('beforeprint', handleLogPrint);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      window.removeEventListener('beforeunload', handleLogArticleView);
      window.removeEventListener('beforeprint', handleLogPrint);
    };
  }, [documentData, isAuthenticated, router.events]);

  useEffect(() => {
    if (isObjectId(documentData.organization_id)) {
      getOrgPage(documentData.organization_id)
        .then(data => setOrgData(data))
        .catch(() => null);
    }
  }, [documentData.organization_id]);

  return (
    <div
      style={{
        display: 'block',
        minHeight: 'calc((100vh - 64px))',
      }}
    >
      <TopSection
        isAuthenticated={Boolean(isAuthenticated)}
        title={documentData?.title || ''}
        titleRef={titleEl}
        doiLink={documentData?.doi}
        pdfUrl={pdfUrl}
        posterId={documentData?.id}
        onBackButtonClick={() => {
          logEvent('BACK_LINK_CLICKED', {
            event_position: 'PRESENTATION_SESSION_PAGE',
          });
          router.push(`/event/${event.id}/submissions`);
        }}
      />
      <Styles.HeroWrapper>
        <Styles.SectionContainer>
          <Styles.ProductLabel className="mb1">{submissionType}</Styles.ProductLabel>
          <Text
            as="h1"
            size="h3_new"
            fontWeight="bold"
            style={{ lineHeight: 1.25, marginBottom: '0.5rem' }}
          >
            <HTMLtext>{documentData?.title || ''}</HTMLtext>
          </Text>
          <div ref={titleEl} />
          {/* {data.uploaded_at ? (
            <Text
              size="body1_new"
              color="grey"
              style={{ lineHeight: 1.25, marginBottom: '0.5rem' }}
            >
              Submitted <FormatDate startDate={data.uploaded_at} endDate={data.uploaded_at} />
            </Text>
          ) : (
            <Text
              size="body1_new"
              color="grey"
              style={{ lineHeight: 1.25, marginBottom: '0.5rem' }}
            >
              No date submitted information is available for this submission.
            </Text>
          )} */}
          {/* TODO replace with userId resolver */}
          <Authors
            eventId={event_id}
            items={authors}
            loading={loadingAuthors}
            embargoed={isEmbargoed}
          />
          <Styles.ArticleSummary>
            <ArticleStatistics stats={stats} />
            <ArticleContents
              poster={{
                ...documentData,
                pdfUrl,
              }}
              videoRef={videoRef}
              posterRef={posterRef}
              abstractRef={abstractRef}
              datasetsRef={datasetsRef}
            />
          </Styles.ArticleSummary>
        </Styles.SectionContainer>
      </Styles.HeroWrapper>
      {isLoadingAccess ? (
        <ArticleSection title="">
          <Styles.ZeroStateBox>
            <Styles.ZeroStateBoxWrapper>
              <SpinnerIcon size="3rem" color="#424d5d" />
            </Styles.ZeroStateBoxWrapper>
          </Styles.ZeroStateBox>
        </ArticleSection>
      ) : (
        <>
          {isEmbargoed && (
            <ArticleSection title="">
              <Styles.ZeroStateBox>
                <Styles.ZeroStateBoxWrapper>
                  <Flexbox justifyContent="space-between" style={{ width: '100%' }}>
                    <Flexbox flexDirection="column" style={{ width: '70%' }}>
                      {isTicketingProduct ? (
                        <>
                          <Text size="body1_new" fontWeight="bold">
                            {event.name} is a Ticketed Event
                          </Text>
                          <Text size="caption_new">
                            Purchase to access restricted content including Live and Networking
                            Sessions. Connect with your peers using messaging and 1-1 meetings.
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text size="body1_new" fontWeight="bold">
                            {event.name} is a Private Event
                          </Text>
                          <Text size="caption_new">
                            Contact the Organizers to request access to this content. In the
                            meantime, be the first to read from over 10,000 pieces of new research
                          </Text>
                        </>
                      )}
                    </Flexbox>
                    {isTicketingProduct ? (
                      <BuyATicketButton />
                    ) : (
                      <Button
                        color="primary"
                        href={isAuthenticated ? '/home' : `${origin}/request-invite`}
                        as="a"
                      >
                        Explore Research
                      </Button>
                    )}
                  </Flexbox>
                </Styles.ZeroStateBoxWrapper>
              </Styles.ZeroStateBox>
            </ArticleSection>
          )}
          {(documentData?.video_presentation?.processing_state === PROCESSING_STATE_DONE ||
            videoSession) &&
          !isEmbargoed ? (
            <>
              {isFreeContent && (
                <ArticleSection
                  title="Free Banner"
                  showTitle={false}
                  style={{ marginBottom: '2.5rem', marginTop: '2.5rem', border: 'none' }}
                >
                  <InformationBanner
                    type="message"
                    style={{ marginBottom: '1rem' }}
                    noBorder
                    backgroundColor="sidebarGrey"
                  >
                    {orgData?.acronym
                      ? `${orgData?.acronym} has opened this video for free access for a limited time. ${orgData?.acronym}'s grant to you
                  of these Licensed Materials extends only to you individually and may not be
                  transferred to, or extended to, others.`
                      : 'This video has free access for a limited time. The use of these Licensed Materials extends only to you individually and may not be transferred to, or extended to, others.'}
                  </InformationBanner>
                </ArticleSection>
              )}
              <ArticleSection
                title={documentData?.video_presentation ? 'Video' : ''}
                ref={videoRef}
                showTitle
              >
                <VideoSection
                  organization_id={documentData?.organization_id}
                  event_id={event_id}
                  article_id={article_id}
                  event={event}
                  articleVideoPresentation={documentData?.video_presentation}
                  videoSession={videoSession}
                  article_title={documentData.title}
                  organization_data={orgData}
                  submissionDate={documentData.submitted_at}
                  isDPCEvent={isDPCEvent}
                  dpcCustomEventName={documentData.dpc_conference_name}
                />
              </ArticleSection>
            </>
          ) : (
            <div style={{ padding: '0 2rem', marginBottom: '32px' }}>
              {event ? (
                <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
                  <Styles.ProductLabel className="mb1">Presented at</Styles.ProductLabel>
                  {isDPCEvent ? (
                    <Text
                      as="a"
                      onClick={() => {
                        logEvent('EVENT_CLICKED', {
                          eventPosition: 'ARTICLE_PAGE',
                          id: event.id,
                        });
                      }}
                      fontWeight="semiBold"
                      style={{
                        fontSize: '1.5rem',
                        lineHeight: '2rem',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {documentData.dpc_conference_name}
                    </Text>
                  ) : (
                    <Link href={`/event/${event.id}`} passHref>
                      <Text
                        as="a"
                        onClick={() => {
                          logEvent('EVENT_CLICKED', {
                            eventPosition: 'ARTICLE_PAGE',
                            id: event.id,
                          });
                        }}
                        fontWeight="semiBold"
                        style={{
                          fontSize: '1.5rem',
                          lineHeight: '2rem',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          marginBottom: '0.5rem',
                        }}
                      >
                        {event.name}
                      </Text>
                    </Link>
                  )}
                  <Text color="grey">
                    <FormatDate startDate={event.start_date} endDate={event.end_date} />
                  </Text>
                </div>
              ) : (
                <Text color="grey">
                  No conference information is available for this submission.
                </Text>
              )}
              {orgData && orgData?.content_library && !isDPCEvent && (
                <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
                  <OrganizationLink
                    organizationData={orgData}
                    contentLibrary={(router.query.contentLibraryTitle as string) || orgData?.name}
                  />
                </div>
              )}
            </div>
          )}
          {documentData.embargoed && !pdfUrl && (
            <ArticleSection title="Presentation" ref={posterRef}>
              <Styles.ZeroStateBox>
                <Styles.ZeroStateBoxWrapper>
                  <Styles.GrayBox />
                  <Styles.TextWrapper>
                    <Text color="grey" style={{ textAlign: 'center' }}>
                      You do not have access to the full Submission.
                    </Text>
                  </Styles.TextWrapper>
                </Styles.ZeroStateBoxWrapper>
              </Styles.ZeroStateBox>
            </ArticleSection>
          )}

          {pdfUrl && (
            <ArticleSection title="Presentation" ref={posterRef}>
              <PresentationSection poster={documentData} pdfUrl={pdfUrl} />
            </ArticleSection>
          )}
          {abstract && (
            <ArticleSection title="Abstract" ref={abstractRef}>
              <Grid>
                <Grid.Unit size={{ medium: 8 / 12 }}>
                  {abstract ? (
                    <MorressierMarkdown
                      source={abstract}
                      customRenderers={{
                        a: ({
                          href,
                          children,
                        }: {
                          href: string;
                          children: React.ReactChildren;
                        }) => (
                          <a target="_blank" href={href} rel="noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                      allowSuperSub={false}
                    />
                  ) : (
                    <Text style={{ marginBottom: '2rem' }} color="grey">
                      No abstract is available for this submission.
                    </Text>
                  )}
                </Grid.Unit>
              </Grid>
            </ArticleSection>
          )}

          {documentData.doi && (
            <ArticleSection title="DOI">
              <Grid>
                <Grid.Unit size={{ small: 1, large: 8 / 12 }}>
                  <DOISection doi={documentData.doi} />
                </Grid.Unit>
              </Grid>
            </ArticleSection>
          )}

          {documentData?.additional_file_urls &&
            documentData?.additional_file_urls.length > 0 &&
            !isEmbargoed && (
              <ArticleSection title="Datasets" ref={datasetsRef}>
                <DatasetsSection poster={documentData} />
              </ArticleSection>
            )}

          {!isEmbargoed && (
            <ArticleSection title="Discussion">
              <CommentsContainer
                posterId={article_id}
                isRtmpStream={false}
                auto_approve_discussions
                authUser={authUser}
                canApprove={isOrganizer || isPosterAuthor}
                event={event}
              />
            </ArticleSection>
          )}
          {keywords?.length > 0 && !isEmbargoed && (
            <ArticleSection title="Keywords">
              <Grid>
                <Grid.Unit size={{ medium: 8 / 12 }}>
                  {keywords?.length ? (
                    keywords.map(keyword => <StatusChipArticle key={keyword} keyword={keyword} />)
                  ) : (
                    <Text style={{ marginBottom: '2rem' }} color="grey">
                      No keywords are available for this submission
                    </Text>
                  )}
                </Grid.Unit>
              </Grid>
            </ArticleSection>
          )}

          {/* // Licences are incorrecly defined for content libraries
          // Praveer: Events manager does seems to have option to alter license, therefore submission-importer can update the license but then I will also have to update all the files with license info and run submission importer again on staging and production to see if there are no inadvertent implications.
          // TODO: better scope for licence definition */}
          {documentData?.license && documentData.fileid && documentData.submission_completed ? (
            <ArticleSection title="License">
              <LicenseSection license={documentData.license} />
            </ArticleSection>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Article;
